import { t } from "@travily/i18n";
import { Avatar, type AvatarColor } from "@ui/components/Avatar";
import { AvatarPicker } from "@ui/components/AvatarPicker";
import { Heading } from "@ui/components/Heading";
import { Icon } from "@ui/components/Icon";
import { ImagePicker, type Selection } from "@ui/components/ImagePicker";
import { StandardTextArea } from "@ui/components/StandardTextArea";
import { StandardTextField } from "@ui/components/StandardTextField";
import { type SwooshColor, SwooshCurve, SwooshSweep, swooshColor } from "@ui/components/Swoosh";

export type { Selection };

export interface ProfileEditorProps {
  // Display
  /** Account avatar color (Profile::AVATAR_COLORS — red | purple | yellow | gray). */
  color: AvatarColor;
  /** Uppercase initial for the default avatar fallback. */
  initial: string;
  /** Placeholder text for the bio textarea. */
  bioPlaceholder: string;
  /** Personal accounts hold no display name or handle, so those fields are hidden. */
  personal: boolean;

  // Name
  /** Current name value. */
  name: string;
  /** Called when the name field changes. */
  onNameChange: (value: string) => void;
  /** Called when the name field loses focus. */
  onNameBlur?: () => void;
  /** Name validation error, if any. */
  nameError?: string;

  // Handle
  /** Current handle value. */
  handle: string;
  /** Called when the handle field changes. */
  onHandleChange: (value: string) => void;
  /** Called when the handle field loses focus. */
  onHandleBlur?: () => void;
  /** Handle validation error, if any. */
  handleError?: string;
  /** Whether the handle has been validated as available. */
  handleValid?: boolean;
  /** Whether the handle differs from the original value. */
  handleChanged: boolean;

  // Avatar
  /** Current avatar image picker selection. */
  avatarSelection: Selection;
  /** Blob URL preview for an uploaded avatar, if any. */
  avatarUploadPreview: string | null;
  /** Called when the avatar selection changes. */
  onAvatarSelectionChange: (selection: Selection) => void;
  /** Called when an avatar file is selected for upload. */
  onAvatarFileSelect: (file: File) => void;
  /** Called when the uploaded avatar is removed. */
  onAvatarRemove: () => void;

  // Cover
  /** Current cover image picker selection. */
  coverSelection: Selection;
  /** Blob URL preview for an uploaded cover image, if any. */
  coverUploadPreview: string | null;
  /** Called when the cover selection changes. */
  onCoverSelectionChange: (selection: Selection) => void;
  /** Called when a cover file is selected for upload. */
  onCoverFileSelect: (file: File) => void;
  /** Called when the uploaded cover image is removed. */
  onCoverRemove: () => void;
  /** Thumbnail URL of the baked current-location map, or null while baking / no location. */
  coverMapPreview: string | null;
  /** Whether the map cover option is selectable (a baked current-location map exists). */
  coverMapAvailable: boolean;
  /** Hint shown over the disabled map tile (e.g. "Add a trip to show your current location"). */
  coverMapDisabledReason?: string;

  // Bio
  /** Current bio value. */
  bio: string;
  /** Called when the bio field changes. */
  onBioChange: (value: string) => void;
  /** Bio validation error, if any. */
  bioError?: string;
}

/** Maps the account color token to the swoosh variant used for the default cover. */
const COVER_SWOOSH_COLORS: Record<string, SwooshColor> = {
  red: "red",
  yellow: "yellow",
  purple: "purple",
};

/** The Profile settings section: every field of the account's public profile. */
export function ProfileEditor({
  color,
  initial,
  bioPlaceholder,
  personal,
  name,
  onNameChange,
  onNameBlur,
  nameError,
  handle,
  onHandleChange,
  onHandleBlur,
  handleError,
  handleValid,
  handleChanged,
  avatarSelection,
  avatarUploadPreview,
  onAvatarSelectionChange,
  onAvatarFileSelect,
  onAvatarRemove,
  coverSelection,
  coverUploadPreview,
  onCoverSelectionChange,
  onCoverFileSelect,
  onCoverRemove,
  coverMapPreview,
  coverMapAvailable,
  coverMapDisabledReason,
  bio,
  onBioChange,
  bioError,
}: ProfileEditorProps) {
  // The default cover previews the same swoosh (and pale tint) the profile renders when no
  // cover image is set, matched to the account color: SwooshStrip's curve on mobile and the
  // SwooshBackground sweep on desktop, mirroring the profile's responsive split.
  const coverSwooshColor = COVER_SWOOSH_COLORS[color] ?? "red";

  return (
    <div>
      <Heading.Root color="primary" size="lg" weight="bold">
        <Heading.Title as="h1">{t("web.accounts.settings.sections.profile")}</Heading.Title>
      </Heading.Root>

      <div className="mt-6 flex flex-col gap-5">
        {!personal && (
          <>
            <StandardTextField
              description={t("components.views.accounts.profile_form.name_description")}
              errorMessage={nameError}
              isInvalid={!!nameError}
              label={t("components.views.accounts.profile_form.name_label")}
              leadIcon={<Icon name="profile" type="sticker" />}
              onBlur={onNameBlur}
              onChange={onNameChange}
              placeholder={t("components.views.accounts.profile_form.name_placeholder")}
              value={name}
            />
            <StandardTextField
              errorMessage={handleChanged ? handleError : undefined}
              isInvalid={handleChanged && !!handleError}
              label={t("components.views.accounts.profile_form.username_label")}
              leadIcon={<Icon name="username" type="sticker" />}
              onBlur={onHandleBlur}
              onChange={onHandleChange}
              placeholder={t("components.views.accounts.profile_form.username_placeholder")}
              successMessage={
                handleChanged && handleValid
                  ? t("components.views.accounts.profile_form.username_available")
                  : undefined
              }
              value={handle}
            />
          </>
        )}
        <AvatarPicker
          defaultTile={
            <Avatar.Root className="size-full rounded-xl text-2xl" color={color}>
              <Avatar.Fallback>{initial}</Avatar.Fallback>
            </Avatar.Root>
          }
          label={t("components.views.accounts.profile_form.upload_profile_picture")}
          onFileSelect={onAvatarFileSelect}
          onRemove={onAvatarRemove}
          onSelectionChange={onAvatarSelectionChange}
          selection={avatarSelection}
          uploadPreview={avatarUploadPreview}
        />
        <ImagePicker
          defaultOption={
            <div
              className={swooshColor({
                color: coverSwooshColor,
                className: "relative size-full overflow-clip rounded-xl bg-white",
              })}
            >
              <SwooshCurve className="size-full md:hidden" />
              {/* Reproduce the desktop SwooshBackground crop inside the tile: same width
                  factor and translate offsets as its full-bleed SWOOSH_STYLE, but as a
                  percentage of the tile rather than the viewport. */}
              <SwooshSweep
                className="absolute hidden h-auto md:block"
                style={{ width: "167.5%", translate: "-25.25% -39.2%" }}
              />
            </div>
          }
          label={t("components.views.accounts.profile_form.choose_cover_image")}
          layout="grid"
          onFileSelect={onCoverFileSelect}
          onRemove={onCoverRemove}
          onSelectionChange={onCoverSelectionChange}
          presetImages={[
            {
              key: "map",
              alt: t("components.views.accounts.profile_form.cover_map_option"),
              content: coverMapPreview ? (
                <img alt="" className="size-full object-cover" src={coverMapPreview} />
              ) : (
                <div className="flex size-full items-center justify-center bg-neutral-100">
                  <Icon className="size-6 text-neutral-400" name="location" type="sticker" />
                </div>
              ),
              disabled: !coverMapAvailable,
              disabledHint: coverMapDisabledReason,
            },
          ]}
          selection={coverSelection}
          uploadPreview={coverUploadPreview}
        />
        <StandardTextArea
          errorMessage={bioError}
          isInvalid={!!bioError}
          label={t("components.views.accounts.profile_form.add_bio")}
          leadIcon={<Icon name="message" type="sticker" />}
          onChange={onBioChange}
          placeholder={bioPlaceholder}
          value={bio}
        />
      </div>
    </div>
  );
}
